import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Menu, X, MessageCircle, LayoutDashboard } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { trackContactClick } from "@/lib/analytics";
import LanguageToggle from "./LanguageToggle";

const WHATSAPP_NUMBER = "963992367582";

const PublicNavbar = () => {
    const { t } = useTranslation();
    const { dir } = useLanguage();
    const localizedPath = useLocalizedPath();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const { isAuthenticated } = useAuth();

    const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        t("common.whatsapp_initial_inquiry"),
    )}`;

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const navLinks: { name: string; path: string }[] = [
        { name: t("nav.home"), path: "/" },
        { name: t("nav.portfolio"), path: "/portfolio" },
        { name: t("nav.blog"), path: "/blog" },
        { name: t("nav.videos"), path: "/videos" },
        { name: t("nav.about"), path: "/about" },
        { name: t("nav.contact"), path: "/contact" },
    ];

    const handleLinkClick = () => {
        setIsMobileMenuOpen(false);
    };

    const isActive = (path: string) => {
        const localized = localizedPath(path);
        return location.pathname === localized;
    };

    return (
        <nav
            className={cn(
                "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
                isScrolled
                    ? "bg-background/40 backdrop-blur-xl border-b border-white/5 py-4 shadow-2xl"
                    : "bg-transparent py-8"
            )}
            dir={dir}
        >
            <div className="container mx-auto px-6 flex items-center justify-between">
                {/* Logo */}
                <Link to={localizedPath("/")} className="flex items-center gap-2 group">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center group-hover:bg-primary transition-colors duration-300">
                        <span className="text-background font-black text-xl">T</span>
                    </div>
                    <span className="text-2xl font-bold tracking-tighter text-white">
                        {t("common.brand")}
                    </span>
                </Link>

                {/* Desktop Links */}
                <div className="hidden md:flex items-center gap-10">
                    {navLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={localizedPath(link.path)}
                            className={cn(
                                "text-sm font-medium transition-all duration-300 relative py-1",
                                "hover:text-white",
                                isActive(link.path)
                                    ? "text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-primary after:rounded-full"
                                    : "text-muted-foreground"
                            )}
                        >
                            {link.name}
                        </Link>
                    ))}
                </div>

                {/* Action Buttons */}
                <div className="hidden md:flex items-center gap-4">
                    {isAuthenticated ? (
                        <Link to="/dashboard">
                            <span className="text-sm font-medium text-muted-foreground hover:text-white transition-colors cursor-pointer flex items-center gap-2">
                                <LayoutDashboard size={16} />
                                {t("common.dashboard")}
                            </span>
                        </Link>
                    ) : (
                        <Link to="/login">
                            <span className="text-sm font-medium text-muted-foreground hover:text-white transition-colors cursor-pointer">
                                {t("common.login")}
                            </span>
                        </Link>
                    )}
                    <LanguageToggle />
                    <a
                        href={whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackContactClick("whatsapp_sy", "navbar")}
                    >
                        <Button className="bg-[#128C7E] text-white hover:bg-[#075E54] font-bold px-6 rounded-full transition-all duration-300 shadow-tech gap-2">
                            <MessageCircle size={18} />
                            {t("common.whatsapp")}
                        </Button>
                    </a>
                </div>

                {/* Mobile Menu Button */}
                <button
                    className="md:hidden w-10 h-10 flex items-center justify-center text-white bg-white/5 rounded-lg border border-white/10"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    aria-label={isMobileMenuOpen ? t("common.close_menu") : t("common.open_menu")}
                    aria-expanded={isMobileMenuOpen}
                    aria-controls="mobile-menu"
                >
                    {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <div
                    id="mobile-menu"
                    role="navigation"
                    aria-label={t("common.main_nav")}
                    className="md:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-2xl border-b border-white/5 p-6 flex flex-col gap-6 animate-in fade-in slide-in-from-top-4 duration-300"
                >
                    {navLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={localizedPath(link.path)}
                            className={cn(
                                "text-lg font-medium transition-colors",
                                isActive(link.path) ? "text-primary" : "text-foreground/80"
                            )}
                            onClick={handleLinkClick}
                        >
                            {link.name}
                        </Link>
                    ))}
                    <div className="flex flex-col gap-4 pt-6 border-t border-white/5">
                        {isAuthenticated ? (
                            <Link to="/dashboard" onClick={handleLinkClick}>
                                <Button variant="ghost" className="w-full justify-start text-muted-foreground gap-2">
                                    <LayoutDashboard size={18} />
                                    {t("common.dashboard")}
                                </Button>
                            </Link>
                        ) : (
                            <Link to="/login" onClick={handleLinkClick}>
                                <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                                    {t("common.login")}
                                </Button>
                            </Link>
                        )}
                        <LanguageToggle variant="mobile" />
                        <a href={whatsappLink} target="_blank" rel="noopener noreferrer" onClick={handleLinkClick}>
                            <Button className="w-full bg-[#128C7E] text-white font-bold h-12 gap-2">
                                <MessageCircle size={18} />
                                {t("common.whatsapp")}
                            </Button>
                        </a>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default PublicNavbar;
